/**
 * Шаблоны фрагментов (ПЛ-03): вставить типовой или свой фрагмент в график за одно действие или сохранить
 * узел ИСР шаблоном компании. Вставка — обычная правка: её видно в журнале и можно откатить.
 */
import type { ScheduleView } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Spinner, toast } from '../../ui/feedback';
import { Input, Select, Textarea } from '../../ui/form';
import { LField } from './LField';

interface Fragment {
  id: string;
  name: string;
  description: string | null;
  paramName: string | null;
  paramUnit: string | null;
  paramDefault: number | null;
  builtin: boolean;
  activityCount: number;
  canDelete: boolean;
}
const num = (s: string) => Number(s.replace(/\s/g, '').replace(',', '.'));

export function FragmentDialog({
  view,
  selectedId,
  onClose,
  onView,
}: {
  view: ScheduleView;
  selectedId: string | null;
  onClose: () => void;
  onView: (v: ScheduleView) => void;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'insert' | 'save'>('insert');
  const list = useQuery({
    queryKey: ['schedule-fragments'],
    queryFn: () => api<Fragment[]>('/schedule/fragments'),
  });
  const sel = selectedId ? view.activities.find((a) => a.id === selectedId) : undefined;
  const [f, setF] = useState({
    templateId: '',
    rootName: '',
    param: '',
    parentWbsId: sel?.wbsId ?? '',
    after: sel?.id ?? '',
  });
  const [s, setS] = useState({
    wbsId: '',
    name: '',
    description: '',
    paramName: '',
    paramUnit: '',
    paramValue: '',
  });
  const t = list.data?.find((x) => x.id === f.templateId);
  const wbsPath = (id: string): string => {
    const w = view.wbs.find((x) => x.id === id);
    if (!w) return '';
    const up = w.parentId ? wbsPath(w.parentId) : '';
    return up ? `${up} / ${w.name}` : w.name;
  };
  const wbsOptions = [...view.wbs]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((w) => ({ id: w.id, title: `${w.code ? `${w.code} ` : ''}${wbsPath(w.id)}` }));

  const insert = useMutation({
    mutationFn: () =>
      api<ScheduleView>(`/schedules/${view.schedule.id}/fragments/insert`, {
        body: {
          templateId: f.templateId,
          rootName: f.rootName.trim() || t?.name || 'Фрагмент',
          parentWbsId: f.parentWbsId || null,
          param: t?.paramName ? num(f.param) || t.paramDefault : null,
          afterActivityId: f.after || null,
        },
      }),
    onSuccess: (v) => {
      onView(v);
      toast('Фрагмент вставлен — отменить можно в журнале правок', 'success');
      onClose();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const save = useMutation({
    mutationFn: () =>
      api<Fragment>(`/schedules/${view.schedule.id}/fragments`, {
        body: {
          wbsId: s.wbsId,
          name: s.name.trim(),
          description: s.description.trim() || null,
          paramName: s.paramName.trim() || null,
          paramUnit: s.paramUnit.trim() || null,
          paramValue: s.paramName.trim() && num(s.paramValue) > 0 ? num(s.paramValue) : null,
        },
      }),
    onSuccess: (x) => {
      qc.invalidateQueries({ queryKey: ['schedule-fragments'] });
      toast(`Шаблон «${x.name}» сохранён: работ ${x.activityCount}`, 'success');
      setMode('insert');
      setF({ ...f, templateId: x.id });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/schedule/fragments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-fragments'] });
      setF({ ...f, templateId: '' });
    },
    onError: (e) => toast(e.message, 'danger'),
  });

  const tabs = (
    <div className="flex gap-1 border-b border-line" role="tablist">
      {(
        [
          ['insert', 'Вставить'],
          ['save', 'Сохранить узел шаблоном'],
        ] as const
      ).map(([k, title]) => (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={mode === k}
          onClick={() => setMode(k)}
          className={`min-h-11 px-3 text-[14px] ${mode === k ? 'border-b-2 border-ink font-medium' : 'text-ink-2'}`}
        >
          {title}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Шаблоны фрагментов"
      description="Типовые камера, резервуар, участок водовода и дороги и шаблоны вашей компании."
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Отмена
          </Button>
          {mode === 'insert' ? (
            <Button
              variant="primary"
              disabled={!f.templateId}
              loading={insert.isPending}
              onClick={() => insert.mutate()}
              className="max-md:h-11"
            >
              Вставить фрагмент
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!s.wbsId || s.name.trim().length < 2}
              loading={save.isPending}
              onClick={() => save.mutate()}
              className="max-md:h-11"
            >
              Сохранить шаблон
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {tabs}
        {list.isPending ? <Spinner /> : null}
        {mode === 'insert' && list.data ? (
          <>
            <LField label="Шаблон">
              <Select
                value={f.templateId}
                onChange={(e) => {
                  const x = list.data.find((y) => y.id === e.target.value);
                  setF({
                    ...f,
                    templateId: e.target.value,
                    rootName: f.rootName || x?.name || '',
                    param: x?.paramDefault != null ? String(x.paramDefault) : '',
                  });
                }}
                className="max-md:h-11"
              >
                <option value="">Выберите шаблон…</option>
                <optgroup label="Типовые">
                  {list.data
                    .filter((x) => x.builtin)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name} · работ {x.activityCount}
                      </option>
                    ))}
                </optgroup>
                {list.data.some((x) => !x.builtin) ? (
                  <optgroup label="Шаблоны компании">
                    {list.data
                      .filter((x) => !x.builtin)
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name} · работ {x.activityCount}
                        </option>
                      ))}
                  </optgroup>
                ) : null}
              </Select>
            </LField>
            {t?.description ? <p className="text-[13px] text-ink-2">{t.description}</p> : null}
            {t?.canDelete ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => window.confirm(`Удалить шаблон «${t.name}»?`) && remove.mutate(t.id)}
                className="self-start max-md:h-11"
              >
                Удалить этот шаблон
              </Button>
            ) : null}
            <LField label="Название узла ИСР на графике">
              <Input
                value={f.rootName}
                maxLength={300}
                onChange={(e) => setF({ ...f, rootName: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            {t?.paramName ? (
              <LField
                label={`${t.paramName}${t.paramUnit ? `, ${t.paramUnit}` : ''}`}
                hint="Объёмы работ и длительности считаются от этого значения"
              >
                <Input
                  inputMode="decimal"
                  value={f.param}
                  onChange={(e) => setF({ ...f, param: e.target.value })}
                  className="w-40 max-md:h-11"
                />
              </LField>
            ) : null}
            <LField label="Куда вставить">
              <Select
                value={f.parentWbsId}
                onChange={(e) => setF({ ...f, parentWbsId: e.target.value })}
                className="max-md:h-11"
              >
                <option value="">В корень графика</option>
                {wbsOptions.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.title}
                  </option>
                ))}
              </Select>
            </LField>
            <LField
              label="Начать после работы"
              hint="Первые работы фрагмента получат связь «окончание — начало»"
            >
              <Select
                value={f.after}
                onChange={(e) => setF({ ...f, after: e.target.value })}
                className="max-md:h-11"
              >
                <option value="">Без связи</option>
                {view.activities.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} {a.name}
                  </option>
                ))}
              </Select>
            </LField>
          </>
        ) : null}
        {mode === 'save' ? (
          <>
            <LField label="Узел ИСР" hint="Узел со всеми подузлами, работами и связями между ними">
              <Select
                value={s.wbsId}
                onChange={(e) => setS({ ...s, wbsId: e.target.value })}
                className="max-md:h-11"
              >
                <option value="">Выберите узел…</option>
                {wbsOptions.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.title}
                  </option>
                ))}
              </Select>
            </LField>
            <LField label="Название шаблона">
              <Input
                value={s.name}
                maxLength={200}
                onChange={(e) => setS({ ...s, name: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Описание">
              <Textarea
                value={s.description}
                maxLength={1000}
                onChange={(e) => setS({ ...s, description: e.target.value })}
              />
            </LField>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <LField label="Параметр объекта" hint="Например, «Длина участка»">
                <Input
                  value={s.paramName}
                  maxLength={80}
                  onChange={(e) => setS({ ...s, paramName: e.target.value })}
                  className="max-md:h-11"
                />
              </LField>
              <LField label="Единица">
                <Input
                  value={s.paramUnit}
                  maxLength={20}
                  onChange={(e) => setS({ ...s, paramUnit: e.target.value })}
                  className="max-md:h-11"
                />
              </LField>
              <LField label="Значение у этого узла" hint="Объёмы работ делятся на него">
                <Input
                  inputMode="decimal"
                  value={s.paramValue}
                  onChange={(e) => setS({ ...s, paramValue: e.target.value })}
                  className="max-md:h-11"
                />
              </LField>
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
